/**
 * Tiny fetch wrapper for the dashboard. Sends cookies, parses JSON,
 * normalizes our { ok: false, error: { code, message } } shape into thrown
 * Errors so callers can use try/catch.
 */

import type { ApiError, ApiOk, ApiResponse } from '@redawn/shared-types';

export class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  let parsed: ApiResponse<T> | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as ApiResponse<T>;
    } catch {
      // body wasn't JSON — fall through
    }
  }

  if (!res.ok) {
    if (parsed && !parsed.ok) {
      const { code, message } = (parsed as ApiError).error;
      throw new ApiCallError(res.status, code, message);
    }
    throw new ApiCallError(res.status, 'HTTP_ERROR', `HTTP ${res.status}`);
  }

  if (parsed && parsed.ok) return (parsed as ApiOk<T>).data;
  // Some endpoints (like /api/health) return raw JSON without the envelope.
  return (parsed as unknown as T) ?? (text as unknown as T);
}
